import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { BRAND_LOGO, shortDate } from "../lib/brand";
import { toast } from "sonner";
import { QrCode, Power, Warning, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const STATUS_STYLES = {
  active: { label: "Active", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  expired: { label: "Expired", className: "bg-red-500/15 text-red-300 border-red-500/40" },
  suspended: { label: "Suspended", className: "bg-red-500/15 text-red-300 border-red-500/40" },
  pending_payment: { label: "Pending", className: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  pending_verification: { label: "Pending", className: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  rejected: { label: "Rejected", className: "bg-red-500/15 text-red-300 border-red-500/40" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status || "Unknown", className: "bg-white/5 text-white/60 border-white/15" };
  return (
    <span className={`text-[10px] tracking-luxe uppercase border rounded-full px-2.5 py-1 ${s.className}`}>
      {s.label}
    </span>
  );
}

export default function Scanner() {
  const { user, logout } = useAuth();
  const [stage, setStage] = useState("idle");
  const [scan, setScan] = useState(null);
  const [recent, setRecent] = useState([]);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const qrInstance = useRef(null);

  const loadRecent = () =>
    api.get("/merchant/transactions-lite")
      .then(({ data }) => setRecent((data || []).slice(0, 10)))
      .catch(() => setRecent([]));

  useEffect(() => {
    loadRecent();
    const t = setTimeout(() => {
      setStage((current) => {
        if (current === "idle") {
          startScan();
        }
        return current;
      });
    }, 400);

    return () => {
      clearTimeout(t);
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScan = async () => {
    await stopScan();
    setStage("scanning");
    setScan(null);

    setTimeout(async () => {
      try {
        const inst = new Html5Qrcode("tsc-qr-reader");
        qrInstance.current = inst;

        await inst.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 320, height: 320 },
            aspectRatio: 1.0,
            disableFlip: false,
          },
          (decoded) => handleDecoded(decoded),
          () => {}
        );
      } catch (e) {
        toast.error("Camera not available. Use manual code entry below.");
        setStage("idle");
      }
    }, 80);
  };

  const stopScan = async () => {
    try {
      if (qrInstance.current) {
        await qrInstance.current.stop();
        await qrInstance.current.clear();
      }
    } catch {}
    qrInstance.current = null;
  };

  const handleDecoded = async (token) => {
    await stopScan();

    try {
      const cleanToken = String(token || "").trim();
      const { data } = await api.post("/scanner/scan", { qr_token: cleanToken });
      setScan({ ...data, qr_token: cleanToken });
      setStage("scanned");
      if (data.warning) toast.warning(data.warning);
      loadRecent();
    } catch (err) {
      toast.error(formatApiError(err));
      setStage("idle");
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manual.trim()) {
      toast.error("Paste QR token first.");
      return;
    }
    handleDecoded(manual.trim());
  };

  const restart = async () => {
    await stopScan();
    setStage("idle");
    setScan(null);
    setManual("");
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/scanner" className="flex items-center gap-2">
            <img src={BRAND_LOGO} alt="" className="h-9 w-9 rounded-full ring-1 ring-[#D4AF37]/40" />
            <span className="font-display text-base text-white">TSC Scanner</span>
          </Link>

          <div className="flex items-center gap-4">
            <Link to="/merchant/transactions" className="text-[10px] tracking-luxe uppercase text-white/70 hover:text-[#D4AF37]">
              History
            </Link>
            <span className="text-[10px] tracking-luxe uppercase text-white/50 hidden sm:inline">{user?.name}</span>
            <button onClick={logout} className="text-white/60 hover:text-white">
              <Power size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6">
        {stage === "idle" && (
          <div className="rise-in">
            <div className="text-center mb-8">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Scanner</p>
              <h1 className="font-display text-4xl text-white mt-2">Scan Member QR</h1>
              <p className="text-white/55 text-sm mt-2">Scan a member QR to verify identity and membership status.</p>
            </div>

            <button onClick={startScan} className="tsc-btn-gold w-full py-5 text-sm flex items-center justify-center gap-3">
              <QrCode size={22} /> Open Camera Scanner
            </button>

            <div className="mt-6 tsc-card p-5">
              <p className="text-[10px] tracking-luxe uppercase text-white/60">Manual entry</p>
              <form onSubmit={submitManual} className="mt-3 flex gap-2">
                <input
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  className="tsc-input flex-1"
                  placeholder="Paste QR token"
                />
                <button className="tsc-btn-outline">Validate</button>
              </form>
            </div>

            <div className="mt-10">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] mb-3">Recent scans</p>
              {recent.length === 0 ? (
                <p className="text-white/50 italic text-sm">No scans yet.</p>
              ) : (
                <div className="tsc-card divide-y divide-white/5">
                  {recent.map((t, i) => (
                    <div key={i} className="px-5 py-3">
                      <p className="text-white">{t.member_name || t.customer_label || "Member"}</p>
                      <p className="text-white/50 text-xs">{shortDate(t.created_at)} · Member</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {stage === "scanning" && (
          <div className="rise-in">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Point camera at member QR</p>
              <button onClick={async () => { await stopScan(); setStage("idle"); }} className="text-white/60">
                <X size={20} />
              </button>
            </div>
            <div id="tsc-qr-reader" ref={scannerRef} className="rounded-md overflow-hidden border border-[#D4AF37]/30 bg-black min-h-[340px]" />
          </div>
        )}

        {stage === "scanned" && scan && (
          <div className="rise-in space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Member Verified</p>
              <button onClick={restart} className="text-white/60 text-[10px] tracking-luxe uppercase">
                Scan Another ↺
              </button>
            </div>

            {scan.warning && (
              <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 p-4 rounded-md flex gap-3">
                <Warning size={22} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Suspicious activity</p>
                  <p className="text-sm mt-1 opacity-90">{scan.warning}</p>
                </div>
              </div>
            )}

            <div className="tsc-card p-6">
              <div className="flex items-start gap-5">
                {scan.member?.identity_photo_url ? (
                  <div className="flex-shrink-0">
                    <div className="h-28 w-28 sm:h-36 sm:w-36 rounded-md overflow-hidden border-2 border-[#D4AF37]/60">
                      <img src={scan.member.identity_photo_url} alt={scan.member.full_name} className="w-full h-full object-cover" />
                    </div>
                    <p className="text-[9px] tracking-luxe uppercase text-[#D4AF37] mt-2 text-center">Verify face</p>
                  </div>
                ) : (
                  <div className="flex-shrink-0 h-28 w-28 sm:h-36 sm:w-36 rounded-md border-2 border-dashed border-amber-500/40 bg-amber-500/5 flex items-center justify-center text-amber-300 text-center px-2">
                    <p className="text-[10px] tracking-luxe uppercase">No selfie · ask for ID</p>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Member</p>
                  <p className="font-display text-2xl sm:text-3xl text-white mt-1 truncate">
                    {scan.member?.full_name || "Member"}
                  </p>
                  <p className="text-white/60 text-sm mt-1">{scan.member?.tier_name || "Member"}</p>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <StatusBadge status={scan.member?.status} />
                    {scan.member?.expiry_date && (
                      <span className="text-[10px] tracking-luxe uppercase text-white/55 border border-white/10 rounded-full px-2.5 py-1">
                        Expires {shortDate(scan.member.expiry_date)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="tsc-card p-6 text-center">
              <p className="text-white/70">Membership verified successfully.</p>
              <button onClick={restart} className="tsc-btn-gold mt-5">
                Scan Another Member
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
